import type { MissedRing } from '@etown/shared';
import { useChatStore } from '../../stores/chatStore';
import { useDmStore } from '../../stores/dmStore';
import { useMissedRingsStore } from '../../stores/missedRingsStore';
import { useOffice } from '../office/OfficeContext';
import './av.css';

/**
 * Wer angeklingelt hat, waehrend man nicht hingesehen hat.
 *
 * Der akute Klingel-Toast ist nach einer halben Minute weg — wer in der Zeit
 * nicht am Platz war, erfuhr bisher nie davon. Diese Karten bleiben stehen,
 * bis man sie quittiert, und ueberleben Neuladen und Abmelden: die Versuche
 * liegen in der Datenbank, nicht im Speicher.
 */
export function MissedRingCards() {
  const entries = useMissedRingsStore((s) => s.entries);
  if (entries.length === 0) return null;

  return (
    <div className="missed-rings">
      {entries.map((entry) => (
        <MissedRingCard key={entry.fromUserId} entry={entry} />
      ))}
    </div>
  );
}

/** „14:32" — der Tag steht nicht dabei, das Datum ergibt sich aus dem Kontext. */
function uhrzeit(atMs: number): string {
  return new Date(atMs).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function MissedRingCard({ entry }: { entry: MissedRing }) {
  const { socket } = useOffice();
  const dismiss = useMissedRingsStore((s) => s.dismiss);

  const quittieren = (): void => {
    // Sofort weg, ohne auf den Server zu warten: eine Quittung, die
    // fehlschlaegt, taucht beim naechsten Anmelden ohnehin wieder auf
    dismiss(entry.fromUserId);
    socket.emit('ring:missed:ack', { fromUserId: entry.fromUserId }, () => undefined);
  };

  const schreiben = (): void => {
    useChatStore.getState().setOpen(true);
    useDmStore.getState().openConversation(entry.fromUserId, entry.displayName);
    quittieren();
  };

  return (
    <div className="missed-ring" role="status">
      <span className="missed-ring__bell" aria-hidden="true">🔔</span>
      <span className="missed-ring__text">
        <strong>{entry.displayName}</strong> hat um {uhrzeit(entry.lastAtMs)} angeklingelt
        {entry.count > 1 && <span className="missed-ring__count"> ({entry.count}×)</span>}
      </span>
      <div className="missed-ring__actions">
        <button className="missed-ring__write" onClick={schreiben}>
          Nachricht schreiben
        </button>
        <button className="missed-ring__dismiss" onClick={quittieren} aria-label="Hinweis wegklicken">
          Weg
        </button>
      </div>
    </div>
  );
}
